import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { Box, ColorNames, ColorShades, Utils } from 'marketpro-design-lib';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { constants } from '../../../../constants';
import { AuthService } from '../../../auth/services/auth.service';
import { GroupedResponse } from '../../models/grouped-statistics-response.model';
import { SimpleStatisticsResponse } from '../../models/simple-statistics-response.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { DataService } from '../../services/data.service';
import { DataUtils } from '../../services/DataUtils';
import { CategoryService } from '../../services/categories.service';
import { SizeOfPrizeGeneralInfo } from '../../models/statistics.model';
import { StatisticsService } from '../../services/statistics.service';

@Component({
  selector: 'mpro-consumer-spending-size-prize',
  templateUrl: './consumer-spending-size-prize.component.html',
  styleUrls: ['./consumer-spending-size-prize.component.scss'],
})
export class ConsumerSpendingSizePrizeComponent implements OnInit, OnDestroy {
  faCaretDown = faCaretDown;

  startYear: number = constants.initialYear;
  endYear: number = constants.initialYear + 1;
  currentYear: number = constants.initialYear;

  peopleGrowth: number = 0;
  spendingGrowth: number = 0;
  spendingPerCapitaGrowth: number = 0;

  startYearOpen: boolean = false;
  endYearOpen: boolean = false;

  years: number[] = [];
  startYears: number[] = [];
  endYears: number[] = [];

  public totalDataCacheByYear: GroupedResponse<SimpleStatisticsResponse>;
  public dataCacheByYear: GroupedResponse<SimpleStatisticsResponse>;
  public headCount: number;
  public headCountPercentage: number;
  public spending: number;
  public spendingPercentage: number;
  public dailySpendingPerCapita: number;
  public totalDailySpendingPerCapita: number;
  public dailySpendingPerCapitaPercentage: number;
  public totalPeopleBox: Box = new Box('', '', 'People');
  public totalSpendingBox: Box = new Box('', '', 'Total daily spending');
  public totalSpendingPerCapitaBox: Box = new Box(
    '',
    'Daily spending per capita'
  );
  @Input() public isFromSizeOfPrize: boolean = false;
  public largerThanAvgBox: Box = new Box('', '');
  public peopleBox: Box = new Box('', '', 'People');
  public spendingBox: Box = new Box('', '', 'Spending');
  public spendingPerCapitaBox: Box = new Box('', '', 'Spending per capita');
  public dataFilters: DataFilters = new DataFilters();
  public sizeOfPrizeGeneralInfo$: Observable<SizeOfPrizeGeneralInfo>;

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    public dataService: DataService,
    private dataFiltersService: DataFiltersService,
    private authService: AuthService,
    private categoryService: CategoryService,
    private statisticsService: StatisticsService
  ) {}

  public ngOnInit(): void {
    this.initYears();
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => {
        this.dataFilters = filters;
        this.statisticsService.getSizeOfPrizeGeneralInfo$.next();
      });
    this.dataService
      .getData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        if (
          data.consumerSpendingData.current &&
          data.consumerSpendingData.total &&
          Object.keys(data.consumerSpendingData.current).length > 0
        ) {
          this.totalDataCacheByYear = data.consumerSpendingData.total;
          this.dataCacheByYear = data.consumerSpendingData.current;
          this.calculateData(this.dataFilters.year);
          this.setTopBox();
          this.setMiddleBox();
          this.setRange();
        }
      });

    this.sizeOfPrizeGeneralInfo$ =
      this.statisticsService.getSizeOfPrizeGeneralInfo$.pipe(
        switchMap(() => this.statisticsService.getSizeOfPrizeGeneralInfo())
      );
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setRange() {
    if (this.dataFilters.year > this.currentYear) {
      if (this.startYear !== this.currentYear) {
        this.setStartYear(this.currentYear);
      }
      this.setEndYear(this.dataFilters.year);
    } else if (this.dataFilters.year < this.currentYear) {
      if (this.endYear !== this.currentYear) {
        this.setEndYear(this.currentYear);
      }
      this.setStartYear(this.dataFilters.year);
    } else {
      this.setStartYear(this.currentYear);
      this.setEndYear(this.currentYear + 1);
    }

    this.setBottomBox();
  }

  private calculateData(year: number) {
    this.headCount = this.getHeadCount(year);
    this.headCountPercentage = this.getHeadCountPercentage(year);
    this.spending = this.getSpending(year);
    this.spendingPercentage = this.getSpendingPercentage();
    this.dailySpendingPerCapita = this.getDailySpendingPerCapita(year);
    this.totalDailySpendingPerCapita = this.getTotalDailySpendingPerCapita();
    this.dailySpendingPerCapitaPercentage =
      this.getDailySpendingPerCapitaPercentage();
    this.spendingGrowth = Utils.annualAverageGrowthRate(
      this.getSpending(this.startYear),
      this.getSpending(this.endYear),
      this.endYear - this.startYear
    );
  }

  private getHeadCount(year: number) {
    return this.dataCacheByYear[year]?.headCount ?? 0;
  }

  private getTotalHeadCount() {
    return this.totalDataCacheByYear[this.dataFilters.year]?.headCount ?? -1;
  }

  private getHeadCountPercentage(year: number) {
    return (100 * this.getHeadCount(year)) / this.getTotalHeadCount();
  }

  private getSpending(year: number) {
    let divider = this.dataFilters.spendingPer ? 1 : Utils.getDaysInAYear(year);
    let yearlySpending = this.dataFilters.currency
      ? this.dataCacheByYear[year]?.spendingCurrentUsd ?? 0
      : this.dataCacheByYear[year]?.spending2021ppp ?? 0;

    if (this.dataFilters.category && this.dataFilters.category !== '0') {
      yearlySpending = this.categoryService.getCategoryData(
        this.totalDataCacheByYear[year]
      );
    }

    return yearlySpending / divider;
  }

  private getTotalSpending() {
    let totalSpending = this.dataFilters.currency
      ? this.totalDataCacheByYear[this.dataFilters.year]?.spendingCurrentUsd ??
        -1
      : this.totalDataCacheByYear[this.dataFilters.year]?.spending2021ppp ?? -1;

    if (this.dataFilters.category && this.dataFilters.category !== '0') {
      totalSpending = this.categoryService.getCategoryData(
        this.totalDataCacheByYear[this.dataFilters.year]
      );
    }

    let divider =
      this.dataFilters.spendingPer && totalSpending != -1
        ? 1
        : Utils.getDaysInAYear(this.dataFilters.year);

    return totalSpending / divider;
  }

  private getSpendingPercentage() {
    return (
      100 * (this.getSpending(this.dataFilters.year) / this.getTotalSpending())
    );
  }

  private getDailySpendingPerCapita(year: number) {
    let divider = this.dataFilters.spendingPer
      ? this.getHeadCount(year) * Utils.getDaysInAYear(year)
      : this.getHeadCount(year);

    return this.getSpending(year) / divider;
  }

  private getTotalDailySpendingPerCapita() {
    let divider = this.dataFilters.spendingPer
      ? this.getTotalHeadCount() * Utils.getDaysInAYear(this.dataFilters.year)
      : this.getTotalHeadCount();

    return this.getTotalSpending() / divider;
  }

  private getDailySpendingPerCapitaPercentage(): number {
    return (
      ((this.getDailySpendingPerCapita(this.dataFilters.year) -
        this.getTotalDailySpendingPerCapita()) /
        this.getTotalDailySpendingPerCapita()) *
      100
    );
  }

  changeStartYearOpen() {
    this.startYearOpen = !this.startYearOpen;
  }

  changeEndYearOpen() {
    this.endYearOpen = !this.endYearOpen;
  }

  setTopBox() {
    const worldName = DataUtils.getWorldName(this.dataFilters, 'the world');
    this.totalSpendingBox.top =
      this.dataFilters.spendingPer === 0
        ? 'Total daily spending'
        : 'Total yearly spending';
    this.totalPeopleBox.middle = Utils.formatNumber(
      this.headCount,
      false,
      '.0-0'
    );
    this.totalPeopleBox.bottom = `${Utils.formatNumber(
      this.headCountPercentage
    )}% of ${worldName} population`;
    this.totalSpendingBox.middle = `$${Utils.formatNumber(
      this.spending,
      true
    )}`;
    this.totalSpendingBox.bottom = `${Utils.formatNumber(
      this.spendingPercentage
    )}% of ${worldName} spending`;
  }

  setMiddleBox() {
    this.totalSpendingPerCapitaBox.middle = `$${Utils.formatNumber(
      this.dailySpendingPerCapita,
      false,
      '.2-2'
    )}`;
    this.largerThanAvgBox.middle = `${
      this.dailySpendingPerCapitaPercentage > 0 ? '+' : ''
    }${Utils.formatNumber(this.dailySpendingPerCapitaPercentage)}%`;
    this.largerThanAvgBox.middleColor =
      this.dailySpendingPerCapitaPercentage < 0
        ? Utils.getColorHex({
            name: ColorNames.Red,
            shade: ColorShades.Shade500,
          })
        : undefined;
    this.largerThanAvgBox.bottom = `compared to $${Utils.formatNumber(
      this.totalDailySpendingPerCapita,
      false,
      '.2-2'
    )} ${DataUtils.getWorldName(this.dataFilters, 'world')} avg`;
  }

  setBottomBox() {
    this.setGrowthChanges();
    const people = `${this.peopleGrowth > 0 ? '+' : ''}${Utils.formatNumber(
      this.peopleGrowth
    )}%`;
    const spending = `${this.spendingGrowth > 0 ? '+' : ''}${Utils.formatNumber(
      this.spendingGrowth
    )}%`;
    const spc = `${
      this.spendingPerCapitaGrowth > 0 ? '+' : ''
    }${Utils.formatNumber(this.spendingPerCapitaGrowth)}%`;
    this.peopleBox.middle = people;
    this.spendingBox.middle = spending;
    this.spendingPerCapitaBox.middle = spc;
  }

  setStartYear(year: number, updateBottomBox: boolean = false) {
    if (this.years[this.years.length - 1] === year) {
      year = year - 1;
    }
    this.startYear = year;
    this.endYears = this.years.filter((x) => x > year);
    this.startYearOpen = false;
    if (updateBottomBox) {
      this.currentYear = year;
      this.setBottomBox();
    }
  }

  setEndYear(year: number, updateBottomBox: boolean = false) {
    this.endYear = year;
    this.startYears = this.years.filter((x) => x < year);
    this.endYearOpen = false;
    if (updateBottomBox) {
      this.currentYear = year;
      this.setBottomBox();
    }
  }

  public getTotalSpendingSize() {
    return `$${Utils.formatNumber(this.spending, true)}`;
  }

  setGrowthChanges() {
    let yearDiff = this.endYear - this.startYear;

    this.peopleGrowth = Utils.annualAverageGrowthRate(
      this.getHeadCount(this.startYear),
      this.getHeadCount(this.endYear),
      yearDiff
    );

    this.spendingGrowth = Utils.annualAverageGrowthRate(
      this.getSpending(this.startYear),
      this.getSpending(this.endYear),
      yearDiff
    );

    this.spendingPerCapitaGrowth = Utils.annualAverageGrowthRate(
      this.getDailySpendingPerCapita(this.startYear),
      this.getDailySpendingPerCapita(this.endYear),
      yearDiff
    );
  }

  public getInfoBoxTextColor(): string {
    switch (this.dataFilters.gender) {
      case 'male':
        return '#458461';
      case 'female':
        return '#A84680';
      default:
        return '#29458C';
    }
  }

  private initYears(): void {
    this.years = Array.from({ length: 19 }, (_, i) => i + 2016);
    this.startYears = this.years.slice(0, -1);
    this.endYears = this.years.slice(1);
  }
}
